import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { usePenalty } from '@/hooks/use-penalty';
import { useEvents } from '@/hooks/use-events';
import { usePayments } from '@/hooks/use-payments';
import { useAdminStats } from '@/hooks/use-admin-stats';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AddPaymentDialog } from '@/components/admin/AddPaymentDialog';
import { MarkPaidDialog } from '@/components/admin/MarkPaidDialog';
import { PaymentsDataControls } from '@/components/admin/PaymentsDataControls';
import { BulkCreatePaymentsDialog } from '@/components/admin/BulkCreatePaymentsDialog';
import { TournamentHistory } from '@/components/admin/TournamentHistory';
import { SignupWindowSettings } from '@/components/admin/SignupWindowSettings';
import { EventOccupancySummary } from '@/components/admin/EventOccupancySummary';
import { EditEventDialog } from '@/components/admin/EditEventDialog';
import { CreateEventDialog } from '@/components/admin/CreateEventDialog';
import { EventListView } from '@/components/admin/EventListView';
import { Users, Calendar, Settings, Database, Shield, Activity, ArrowLeft, AlertTriangle, Clock, UserX, DollarSign, CreditCard, Plus, CheckCircle, ArrowUpDown, Upload, Download, RefreshCw, Trophy, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatEventDate } from '@/utils/dateUtils';
import { PaymentRecord } from '@/types/payment';

export default function Admin() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { addPenalty, penalties, getPenaltyStats } = usePenalty();
  const { events } = useEvents();
  const { payments, getMembershipSummaries, getPaymentStats } = usePayments();
  const adminStats = useAdminStats();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'pending' | 'overdue'>('all');
  const [sortBy, setSortBy] = useState<'dueDate' | 'amount' | 'paymentDate' | 'createdAt'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState<any>(null);
  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false);

  const handleEventUpdated = () => {
    // Close dialogs and let useEvents refresh
    setEditEventDialogOpen(false);
    setCreateEventDialogOpen(false);
    setSelectedEventForEdit(null);
  };

  const handleAddNoShowPenalty = (memberId: string, memberName: string, eventId: string, eventName: string, eventDate: string) => {
    addPenalty({
      memberId,
      eventId,
      eventName,
      eventDate,
      type: 'no_show',
      reason: 'Did not show up to event',
      adminId: user?.id
    });
    
    toast.success(t('penalty.admin.added', { memberName }));
    setSelectedEvent('');
    setSelectedParticipant('');
  };

  const penaltyStats = getPenaltyStats();
  const paymentStats = getPaymentStats();
  const membershipSummaries = getMembershipSummaries();

  const filteredPayments = [...payments]
    .filter((p) => (statusFilter === 'all' ? true : p.status === statusFilter))
    .filter((p) => {
      const q = searchQuery.trim().toLowerCase();
      return !q || p.memberName.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      if (sortBy === 'amount') {
        av = a.amount;
        bv = b.amount;
      } else {
        const ad = (a as any)[sortBy] as string | undefined;
        const bd = (b as any)[sortBy] as string | undefined;
        av = ad ? new Date(ad).getTime() : 0;
        bv = bd ? new Date(bd).getTime() : 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleMarkAsPaid = (payment: PaymentRecord) => {
    setSelectedPayment(payment);
    setShowMarkPaidDialog(true);
  };

  // Get past events (within last 30 days) for no-show penalty assignment
  const pastEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    return eventDate < today && eventDate > thirtyDaysAgo;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const selectedEventData = pastEvents.find(event => event.id.toString() === selectedEvent);
  const participants = selectedEventData?.participantDetails || [];

  // Calculate event stats (needs to be before early return)
  const eventStats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const upcoming = events.filter(event => event.date.getTime() >= now.getTime());

    if (!upcoming.length) {
      return null;
    }

    const totalSlots = upcoming.reduce((sum, event) => sum + (event.maxParticipants || 0), 0);
    const totalRegistrations = upcoming.reduce((sum, event) => sum + (event.participants?.length || 0), 0);
    const waitlistCount = upcoming.reduce((sum, event) => sum + ((event.waitingList || []).length), 0);
    const fullEvents = upcoming.filter(event => (event.participants?.length || 0) >= event.maxParticipants).length;

    const highDemandEvents = upcoming
      .map(event => {
        const participants = event.participants?.length || 0;
        const capacity = event.maxParticipants || 0;
        const occupancy = capacity > 0 ? Math.round((participants / capacity) * 100) : 0;
        const waitlist = event.waitingList?.length || 0;
        return {
          id: event.id,
          name: event.title,
          occupancy,
          waitlist,
        };
      })
      .filter(event => event.occupancy >= 75 || event.waitlist > 0)
      .sort((a, b) => b.occupancy - a.occupancy)
      .slice(0, 5);

    return {
      totalSlots,
      totalRegistrations,
      waitlistCount,
      fullEvents,
      upcomingCount: upcoming.length,
      highDemandEvents,
    };
  }, [events]);

  if (!user?.isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">{t("admin.access_denied")}</h2>
          <p className="text-muted-foreground">
            {t("admin.access_denied.message")}
          </p>
        </div>
      </div>
    );
  }

  const systemStats = [
    {
      title: t("admin.total_users"),
      value: adminStats.loading ? "..." : adminStats.totalUsers.toString(),
      description: t("admin.registered_members"),
      icon: Users,
      trend: adminStats.loading ? "..." : `+${adminStats.usersThisMonth} ${t("admin.this_month")}`
    },
    {
      title: t("members.admins"),
      value: adminStats.loading ? "..." : adminStats.adminCount.toString(),
      description: t("members.admins"),
      icon: Shield,
      trend: adminStats.loading ? "..." : ''
    },
    {
      title: t("admin.active_groups"),
      value: adminStats.loading
        ? "..."
        : t("admin.stats.groups_subgroups", { groups: adminStats.totalGroups, subgroups: adminStats.subgroups }),
      description: t("admin.including_subgroups"),
      icon: Users,
      trend: adminStats.loading ? "..." : `+${adminStats.newGroupsThisMonth} ${t("admin.new_groups")}`
    },
    {
      title: t("admin.total_events"),
      value: adminStats.loading ? "..." : adminStats.totalEvents.toString(),
      description: t("admin.all_time_events"),
      icon: Calendar,
      trend: adminStats.loading ? "..." : `+${adminStats.eventsThisMonth} ${t("admin.this_month")}`
    },
    {
      title: t("admin.system_health"),
      value: t("admin.stats.health.excellent"),
      description: t("admin.all_systems_operational"),
      icon: Activity,
      trend: t("admin.stats.uptime", { percent: "99.9" })
    }
  ];

  const handleResetSystem = () => {
    if (window.confirm(t("admin.reset_system.confirm") || "Are you sure you want to reset the system? This will clear all user data and events.")) {
      try {
        // Clear all localStorage data
        localStorage.removeItem('user');
        localStorage.removeItem('events');
        localStorage.removeItem('tournament');
        localStorage.removeItem('tournamentData');
        localStorage.removeItem('tournament_state');
        localStorage.removeItem('eventsDataVersion');
        localStorage.removeItem('membership_payments');
        
        toast.success(t("admin.reset_system.success") || "System reset successfully");
        
        // Redirect to login page after a short delay
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Force reload to clear all state
        }, 1000);
      } catch (error) {
        console.error('Error resetting system:', error);
        toast.error(t("admin.reset_system.error") || "Failed to reset system");
      }
    }
  };

  return (
    <div className="space-y-6 px-4 md:px-0">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("nav.back")}
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t("admin.title")}</h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">
              {t("admin.subtitle")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {systemStats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
              <Badge variant="secondary" className="text-xs mt-2">
                {stat.trend}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop: Tabs */}
      <div className="hidden md:block">
        <Tabs defaultValue="payments" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {t("admin.tabs.payments")}
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t("admin.tabs.events")}
            </TabsTrigger>
            <TabsTrigger value="tournaments" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              {t("admin.tabs.tournaments")}
            </TabsTrigger>
            <TabsTrigger value="penalties">{t("admin.tabs.penalties")}</TabsTrigger>
          </TabsList>

        <TabsContent value="payments">
          {/* Payment Tracking Section */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t("admin.payments.total_revenue")}
                </CardTitle>
                <CardDescription>{t("admin.payments.revenue_description")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${paymentStats.totalRevenue}</div>
                <p className="text-xs text-muted-foreground">
                  {paymentStats.paidPayments} {t("admin.payments.payments_received")}
                </p>
                <Badge variant="secondary" className="text-xs mt-2">
                  {paymentStats.paymentRate}% {t("admin.payments.payment_rate")}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("admin.payments.pending_payments")}
                </CardTitle>
                <CardDescription>{t("admin.payments.outstanding_dues")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${paymentStats.pendingRevenue}</div>
                <p className="text-xs text-muted-foreground">
                  {paymentStats.pendingPayments + paymentStats.overduePayments} {t("admin.payments.outstanding")}
                </p>
                <Badge variant={paymentStats.overduePayments > 0 ? "destructive" : "secondary"} className="text-xs mt-2">
                  {paymentStats.overduePayments} {t("admin.payments.overdue")}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t("admin.payments.member_status")}
                </CardTitle>
                <CardDescription>{t("admin.payments.current_status")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("admin.payments.status.current")}</span>
                    <Badge variant="secondary">
                      {membershipSummaries.filter(m => m.status === 'current').length}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t("admin.payments.status.pending")}</span>
                    <Badge variant="outline">
                      {membershipSummaries.filter(m => m.status === 'pending').length}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t("admin.payments.status.overdue")}</span>
                    <Badge variant="destructive">
                      {membershipSummaries.filter(m => m.status === 'overdue').length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  {t("admin.payments.quick_actions")}
                </CardTitle>
                <CardDescription>{t("admin.payments.management_tools")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full" 
                  size="sm"
                  onClick={() => setShowAddPaymentDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("admin.payments.add_payment")}
                </Button>
                {/* Payments data controls */}
                <PaymentsDataControls 
                  payments={payments}
                  onOpenBulkCreate={() => setShowBulkCreateDialog(true)}
                />
              </CardContent>
            </Card>
          </div>

          {/* Payment Management Tables */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("admin.payments.all_records")}
                </CardTitle>
                <CardDescription>
                  {t("admin.payments.history_tracking")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-3 mb-4">
                  <Input
                    placeholder={t("admin.payments.search_placeholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder={t("admin.payments.filter.status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("admin.payments.status.all")}</SelectItem>
                      <SelectItem value="paid">{t("admin.payments.status.paid")}</SelectItem>
                      <SelectItem value="pending">{t("admin.payments.status.pending")}</SelectItem>
                      <SelectItem value="overdue">{t("admin.payments.status.overdue")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder={t("admin.payments.sort_by")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dueDate">{t("admin.payments.sort.due_date")}</SelectItem>
                      <SelectItem value="paymentDate">{t("admin.payments.sort.paid_date")}</SelectItem>
                      <SelectItem value="createdAt">{t("admin.payments.sort.created")}</SelectItem>
                      <SelectItem value="amount">{t("admin.payments.sort.amount")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                    className="md:ml-auto"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    {sortDir === 'asc' ? t("admin.payments.sort.asc") : t("admin.payments.sort.desc")}
                  </Button>
                </div>

                <ResponsiveTable
                  data={filteredPayments}
                  keyField="id"
                  columns={[
                    { key: 'memberName', label: t("admin.payments.table.member") },
                    { 
                      key: 'amount', 
                      label: t("admin.payments.table.amount"),
                      render: (value, payment) => `$${payment.amount}` 
                    },
                    { 
                      key: 'dueDate', 
                      label: t("admin.payments.table.due_date"),
                      render: (value, payment) => new Date(payment.dueDate).toLocaleDateString() 
                    },
                    { 
                      key: 'paymentDate', 
                      label: t("admin.payments.table.paid_date"),
                      render: (value, payment) => payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-' 
                    },
                    { 
                      key: 'status', 
                      label: t("admin.payments.table.status"),
                      render: (value, payment) => (
                        <Badge 
                          variant={
                            payment.status === 'paid' ? 'default' :
                            payment.status === 'overdue' ? 'destructive' : 'secondary'
                          }
                        >
                          {payment.status}
                        </Badge>
                      )
                    }
                  ]}
                  cardTitle={(payment) => payment.memberName}
                  cardDescription={(payment) => `$${payment.amount} - Due: ${new Date(payment.dueDate).toLocaleDateString()}`}
                  actions={(payment) => (
                    <div className="flex gap-2">
                      {payment.status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => handleMarkAsPaid(payment)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t("admin.payments.mark_paid")}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t("admin.payments.member_summary")}
                </CardTitle>
                <CardDescription>
                  {t("admin.payments.member_overview")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveTable
                  data={membershipSummaries}
                  keyField="memberId"
                  columns={[
                    { key: 'memberName', label: t("admin.payments.table.member") },
                    { 
                      key: 'totalPaid', 
                      label: t("admin.payments.total_paid"),
                      render: (value, summary) => `$${summary.totalPaid}` 
                    },
                    { 
                      key: 'totalDue', 
                      label: t("admin.payments.amount_due"),
                      render: (value, summary) => summary.totalDue > 0 ? `$${summary.totalDue}` : '-' 
                    },
                    { 
                      key: 'lastPaymentDate', 
                      label: t("admin.payments.last_payment"),
                      render: (value, summary) => summary.lastPaymentDate ? new Date(summary.lastPaymentDate).toLocaleDateString() : t("admin.payments.never") 
                    },
                    { 
                      key: 'status', 
                      label: t("admin.payments.table.status"),
                      render: (value, summary) => (
                        <Badge 
                          variant={
                            summary.status === 'current' ? 'default' :
                            summary.status === 'overdue' ? 'destructive' : 'secondary'
                          }
                        >
                          {summary.status}
                        </Badge>
                      )
                    }
                  ]}
                  cardTitle={(summary) => summary.memberName}
                  cardDescription={(summary) => `${t("admin.payments.total_paid")}: $${summary.totalPaid}${summary.totalDue > 0 ? ` • ${t("admin.payments.amount_due")}: $${summary.totalDue}` : ''}`}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="events">
          {/* Event Management Section */}
          <div className="space-y-6">
            {/* Event Management Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">{t("admin.events.title")}</h2>
                <p className="text-sm text-muted-foreground">{t("admin.events.description")}</p>
              </div>
              <Button onClick={() => setCreateEventDialogOpen(true)} className="w-full sm:w-auto shrink-0">
                <CalendarPlus className="h-4 w-4 mr-2" />
                {t('admin.create_new_event')}
              </Button>
            </div>
            
            <SignupWindowSettings />
            
            {(() => {
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              const upcoming = events.filter(event => event.date.getTime() >= now.getTime());

              if (!upcoming.length) {
                return null;
              }

              const totalSlots = upcoming.reduce((sum, event) => sum + (event.maxParticipants || 0), 0);
              const totalRegistrations = upcoming.reduce((sum, event) => sum + (event.participants?.length || 0), 0);
              const waitlistCount = upcoming.reduce((sum, event) => sum + ((event.waitingList || []).length), 0);
              const fullEvents = upcoming.filter(event => (event.participants?.length || 0) >= event.maxParticipants).length;

              const highDemandEvents = upcoming
                .map(event => {
                  const participants = event.participants?.length || 0;
                  const capacity = event.maxParticipants || 0;
                  const occupancy = capacity > 0 ? Math.round((participants / capacity) * 100) : 0;
                  const waitlist = event.waitingList?.length || 0;
                  return {
                    id: event.id,
                    name: event.title,
                    occupancy,
                    waitlist,
                  };
                })
                .filter(item => item.waitlist > 0 || item.occupancy >= 75)
                .sort((a, b) => b.occupancy - a.occupancy)
                .slice(0, 3);

              const handleEditEvent = (eventId: string) => {
                const event = events.find(e => e.id === eventId);
                if (event) {
                  setSelectedEventForEdit(event);
                  setEditEventDialogOpen(true);
                }
              };

              return (
                <>
                  <EventOccupancySummary
                    totalEvents={upcoming.length}
                    totalSlots={totalSlots}
                    totalRegistrations={totalRegistrations}
                    waitlistCount={waitlistCount}
                    fullEvents={fullEvents}
                    highDemandEvents={highDemandEvents}
                    onEditEvent={handleEditEvent}
                  />
                  <EditEventDialog
                    open={editEventDialogOpen}
                    onOpenChange={setEditEventDialogOpen}
                    event={selectedEventForEdit}
                    onEventUpdated={() => {
                      window.location.reload();
                    }}
                  />
                  <CreateEventDialog
                    open={createEventDialogOpen}
                    onOpenChange={setCreateEventDialogOpen}
                    onEventCreated={() => window.location.reload()}
                  />
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* Event List Tab */}
        <TabsContent value="event-list">
          <EventListView />
        </TabsContent>

        {/* Event List Tab */}
        <TabsContent value="event-list">
          <EventListView />
        </TabsContent>

        <TabsContent value="penalties">
          {/* Penalty Management Section */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  {t('penalty.admin.title')}
                </CardTitle>
                <CardDescription>
                  {t('penalty.admin.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{penaltyStats.totalDots}</div>
                    <div className="text-sm text-muted-foreground">{t('penalty.admin.total_dots')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{penaltyStats.activePenalties}</div>
                    <div className="text-sm text-muted-foreground">{t('penalty.admin.active_penalties')}</div>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('penalty.admin.late_cancels')}</span>
                    <Badge variant="outline">{penaltyStats.penaltiesByType.late_cancel}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('penalty.admin.no_shows')}</span>
                    <Badge variant="outline">{penaltyStats.penaltiesByType.no_show}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="h-5 w-5" />
                  {t('penalty.admin.assign_no_show')}
                </CardTitle>
                <CardDescription>
                  {t('penalty.admin.assign_no_show_description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('penalty.admin.select_event')}</label>
                  <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('penalty.admin.select_event_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {pastEvents.map(event => (
                        <SelectItem key={event.id} value={event.id.toString()}>
                          {event.title} - {formatEventDate(event.date)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedEvent && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('penalty.admin.select_member')}</label>
                    <Select value={selectedParticipant} onValueChange={setSelectedParticipant}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('penalty.admin.select_member_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {participants.map(participant => (
                          <SelectItem key={participant.id} value={participant.id}>
                            {participant.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {selectedEvent && selectedParticipant && (
                  <Button 
                    onClick={() => {
                      const event = selectedEventData!;
                      const participant = participants.find(p => p.id === selectedParticipant)!;
                      handleAddNoShowPenalty(
                        participant.id,
                        participant.name,
                        event.id.toString(),
                        event.title,
                        event.date.toISOString()
                      );
                    }}
                    className="w-full"
                    variant="destructive"
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    {t('penalty.admin.assign_penalty')}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('penalty.admin.recent_penalties')}
                </CardTitle>
                <CardDescription>
                  {t('penalty.admin.recent_penalties_description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {penalties.slice(0, 5).map(penalty => (
                    <div key={penalty.memberId} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{t("admin.penalties.member_id", { id: penalty.memberId })}</div>
                        <div className="text-xs text-muted-foreground">
                          {penalty.totalDots} {penalty.totalDots === 1 ? t("admin.penalties.dot") : t("admin.penalties.dots")}
                          {penalty.activePenaltyLevel > 0 && (
                            <span className="ml-2 text-red-600">
                              {t("admin.penalties.level_penalty", { level: penalty.activePenaltyLevel })}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge variant={penalty.activePenaltyLevel > 0 ? "destructive" : "secondary"}>
                        {penalty.activePenaltyLevel > 0 ? t('penalty.status.active') : t('penalty.status.inactive')}
                      </Badge>
                    </div>
                  ))}
                  {penalties.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      {t('penalty.admin.no_penalties')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tournaments" className="space-y-6">
          <TournamentHistory />
        </TabsContent>

        <TabsContent value="system">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t("admin.system.user_management")}
                </CardTitle>
                <CardDescription>
                  {t("admin.system.user_management_desc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{t("admin.system.active_users")}</div>
                    <div className="text-sm text-muted-foreground">{t("admin.system.currently_online", { count: 23 })}</div>
                  </div>
                  <Button variant="outline" size="sm">
                    {t("admin.system.view_all")}
                  </Button>
                </div>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{t("admin.system.new_registrations")}</div>
                    <div className="text-sm text-muted-foreground">{t("admin.system.pending_approval", { count: 3 })}</div>
                  </div>
                  <Button variant="outline" size="sm">
                    {t("admin.system.review")}
                  </Button>
                </div>
                <Button className="w-full">
                  {t("admin.system.manage_users")}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  {t("admin.system.settings")}
                </CardTitle>
                <CardDescription>
                  {t("admin.system.settings_desc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{t("admin.system.email_notifications")}</div>
                    <div className="text-sm text-muted-foreground">{t("admin.system.status_enabled")}</div>
                  </div>
                  <Button variant="outline" size="sm">
                    {t("admin.system.configure")}
                  </Button>
                </div>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{t("admin.system.data_backup")}</div>
                    <div className="text-sm text-muted-foreground">{t("admin.system.last_backup", { time: t("admin.system.time_ago.hours", { count: 2 }) })}</div>
                  </div>
                  <Button variant="outline" size="sm">
                    {t("admin.system.backup_now")}
                  </Button>
                </div>
                <Button className="w-full">
                  {t("admin.system.settings")}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  {t("admin.system.data_management")}
                </CardTitle>
                <CardDescription>
                  {t("admin.system.data_management_desc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full justify-start">
                  {t("admin.system.export_all")}
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  {t("admin.system.import_data")}
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  {t("admin.system.database_cleanup")}
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => {
                  localStorage.removeItem('tournament_state');
                  toast.success(t("admin.system.cache_cleared"));
                }}>
                  {t("admin.system.clear_tournament_cache")}
                </Button>
                <Button variant="destructive" className="w-full justify-start" onClick={handleResetSystem}>
                  {t("admin.system.reset_system")}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  {t("admin.system.recent_activity")}
                </CardTitle>
                <CardDescription>
                  {t("admin.system.activity_log")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm">
                    <div className="font-medium">{t("admin.system.user_registered")}</div>
                    <div className="text-xs text-muted-foreground">{t("admin.system.time_ago.minutes", { count: 2 })}</div>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{t("admin.system.event_created")}</div>
                    <div className="text-xs text-muted-foreground">{t("admin.system.time_ago.minutes", { count: 15 })}</div>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{t("admin.system.group_updated")}</div>
                    <div className="text-xs text-muted-foreground">{t("admin.system.time_ago.hours", { count: 1 })}</div>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{t("admin.system.backup_completed")}</div>
                    <div className="text-xs text-muted-foreground">{t("admin.system.time_ago.hours", { count: 2 })}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </div>

      {/* Mobile: Scrolling sections (most important first) */}
      <div className="md:hidden space-y-8">
        {/* Events Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t("admin.mobile.events")}
          </h2>
          
          <div className="flex flex-col gap-4">
            <Button onClick={() => setCreateEventDialogOpen(true)} className="w-full">
              <CalendarPlus className="h-4 w-4 mr-2" />
              {t('admin.create_new_event')}
            </Button>
          </div>

          <SignupWindowSettings />

          {(() => {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const upcoming = events.filter(event => event.date.getTime() >= now.getTime());

            if (!upcoming.length) {
              return null;
            }

            const totalSlots = upcoming.reduce((sum, event) => sum + (event.maxParticipants || 0), 0);
            const totalRegistrations = upcoming.reduce((sum, event) => sum + (event.participants?.length || 0), 0);
            const waitlistCount = upcoming.reduce((sum, event) => sum + ((event.waitingList || []).length), 0);
            const fullEvents = upcoming.filter(event => (event.participants?.length || 0) >= event.maxParticipants).length;

            const highDemandEvents = upcoming
              .map(event => {
                const participants = event.participants?.length || 0;
                const capacity = event.maxParticipants || 0;
                const occupancy = capacity > 0 ? Math.round((participants / capacity) * 100) : 0;
                const waitlist = event.waitingList?.length || 0;
                return {
                  id: event.id,
                  name: event.title,
                  occupancy,
                  waitlist,
                };
              })
              .filter(item => item.waitlist > 0 || item.occupancy >= 75)
              .sort((a, b) => b.occupancy - a.occupancy)
              .slice(0, 3);

            const handleEditEvent = (eventId: string) => {
              const event = events.find(e => e.id === eventId);
              if (event) {
                setSelectedEventForEdit(event);
                setEditEventDialogOpen(true);
              }
            };

            return (
              <EventOccupancySummary
                totalEvents={upcoming.length}
                totalSlots={totalSlots}
                totalRegistrations={totalRegistrations}
                waitlistCount={waitlistCount}
                fullEvents={fullEvents}
                highDemandEvents={highDemandEvents}
                onEditEvent={handleEditEvent}
              />
            );
          })()}
        </div>

        {/* Payments Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t("admin.mobile.payments")}
          </h2>
          
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="h-4 w-4" />
                  {t("admin.payments.total_revenue")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${paymentStats.totalRevenue}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {paymentStats.paidPayments} {t("admin.payments.payments_received")} • {paymentStats.paymentRate}% {t("admin.payments.payment_rate")}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="h-4 w-4" />
                  {t("admin.payments.status.pending")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${paymentStats.pendingRevenue}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {paymentStats.pendingPayments + paymentStats.overduePayments} {t("admin.payments.outstanding")}
                  {paymentStats.overduePayments > 0 && ` • ${paymentStats.overduePayments} ${t("admin.payments.overdue")}`}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Plus className="h-4 w-4" />
                  {t("admin.payments.quick_actions")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full" 
                  size="sm"
                  onClick={() => setShowAddPaymentDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("admin.payments.add_payment")}
                </Button>
                <PaymentsDataControls 
                  payments={payments}
                  onOpenBulkCreate={() => setShowBulkCreateDialog(true)}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tournaments Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {t("admin.mobile.tournaments")}
          </h2>
          <TournamentHistory />
        </div>

        {/* Penalties Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {t("admin.mobile.penalties")}
          </h2>
          
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4" />
                  {t("admin.penalties.overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{penaltyStats.totalDots}</div>
                    <div className="text-xs text-muted-foreground">{t('penalty.admin.total_dots')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{penaltyStats.activePenalties}</div>
                    <div className="text-xs text-muted-foreground">{t('penalty.admin.active_penalties')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {pastEvents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <UserX className="h-4 w-4" />
                    {t('penalty.admin.assign_no_show')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('penalty.admin.select_event_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {pastEvents.map(event => (
                        <SelectItem key={event.id} value={event.id.toString()}>
                          {event.title} - {formatEventDate(event.date)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedEvent && (
                    <>
                      <Select value={selectedParticipant} onValueChange={setSelectedParticipant}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('penalty.admin.select_member_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {participants.map(participant => (
                            <SelectItem key={participant.id} value={participant.id}>
                              {participant.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {selectedParticipant && (
                        <Button 
                          onClick={() => {
                            const event = selectedEventData!;
                            const participant = participants.find(p => p.id === selectedParticipant)!;
                            handleAddNoShowPenalty(
                              participant.id,
                              participant.name,
                              event.id.toString(),
                              event.title,
                              event.date.toISOString()
                            );
                          }}
                          className="w-full"
                          variant="destructive"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          {t('penalty.admin.assign_penalty')}
                        </Button>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <AddPaymentDialog 
        open={showAddPaymentDialog} 
        onOpenChange={setShowAddPaymentDialog} 
      />
      <MarkPaidDialog 
        open={showMarkPaidDialog} 
        onOpenChange={setShowMarkPaidDialog}
        payment={selectedPayment}
      />
      <BulkCreatePaymentsDialog
        open={showBulkCreateDialog}
        onOpenChange={setShowBulkCreateDialog}
      />
      <CreateEventDialog
        open={createEventDialogOpen}
        onOpenChange={setCreateEventDialogOpen}
        onEventCreated={handleEventUpdated}
      />
      <EditEventDialog
        open={editEventDialogOpen}
        onOpenChange={setEditEventDialogOpen}
        event={selectedEventForEdit}
        onEventUpdated={handleEventUpdated}
      />
    </div>
  );
}
